// Sugalarm — Settings → Account → Passkeys (cycle 152).
//
// Add/list/remove the signed-in user's passkeys. Renders only on
// web + mobile (the kiosk authenticates via device-token pairing and
// never offers passkeys — the caller gates on variant), and only when
// the server reports passkeys configured AND this browser/origin can
// actually do the ceremony (PublicKeyCredential + an origin the RP
// covers) — otherwise the card explains why it's unavailable instead
// of offering a button that can only fail.

import { useCallback, useEffect, useState } from "react";

import * as authApi from "../../api/auth";
import type { PasskeyCredentialOut } from "../../api/auth";
import { isCapacitorNative } from "../../lib/capacitor";
import { useConfirm } from "../../lib/confirmContext";
import {
  decodeCreationOptions,
  encodeAttestation,
  originMatchesRpId,
  supportsPasskeys,
} from "../../lib/webauthn";
import styles from "./SettingsScreen.module.css";

export function PasskeysSection() {
  const { confirm } = useConfirm();
  const [available, setAvailable] = useState<boolean | null>(null);
  const [creds, setCreds] = useState<PasskeyCredentialOut[]>([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const r = await authApi.passkeyCredentials();
    if (r.ok) setCreds(r.data);
  }, []);

  useEffect(() => {
    // v0.148.1: iOS doesn't expose WebAuthn to the native shell's
    // WKWebView — the ceremony can only fail there. Native passkey
    // support is the dedicated next cycle.
    if (isCapacitorNative()) {
      setAvailable(false);
      return;
    }
    void authApi.providers().then((r) => {
      const ok =
        r.ok &&
        r.data.passkey &&
        supportsPasskeys() &&
        originMatchesRpId(r.data.rp_id);
      setAvailable(ok);
      if (ok) void reload();
    });
  }, [reload]);

  const addPasskey = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const begin = await authApi.passkeyRegisterBegin();
      if (!begin.ok) {
        throw new Error(begin.error.message || "Could not start");
      }
      const cred = (await navigator.credentials.create({
        publicKey: decodeCreationOptions(begin.data.options),
      })) as PublicKeyCredential | null;
      if (!cred) throw new Error("No passkey created");
      const r = await authApi.passkeyRegisterFinish(
        begin.data.challenge_id,
        encodeAttestation(cred),
        label.trim() || undefined,
      );
      if (!r.ok) throw new Error(r.error.message || "Could not save passkey");
      setLabel("");
      setNotice("Passkey added.");
      await reload();
    } catch (e) {
      // NotAllowedError = the user dismissed the platform sheet; an
      // InvalidStateError = this authenticator is already registered.
      const name = e instanceof Error ? e.name : "";
      if (name === "NotAllowedError") {
        /* dismissed — no error banner */
      } else if (name === "InvalidStateError") {
        setError("This device already has a passkey for your account.");
      } else {
        setError(e instanceof Error ? e.message : "Could not add a passkey");
      }
    } finally {
      setBusy(false);
    }
  }, [busy, label, reload]);

  const remove = useCallback(
    async (c: PasskeyCredentialOut) => {
      const name = c.label || "this passkey";
      if (
        !(await confirm({
          message: `Remove ${name}? You won't be able to sign in with it anymore.`,
          destructive: true,
        }))
      ) {
        return;
      }
      const r = await authApi.deletePasskeyCredential(c.id);
      if (!r.ok) {
        setError(r.error.message || "Could not remove the passkey");
        return;
      }
      setNotice("Passkey removed.");
      await reload();
    },
    [reload, confirm],
  );

  if (available === false) {
    return (
      <div className={styles.subtleHint}>
        Passkeys aren’t available here yet — manage them at
        app.sugalarm.com in your browser. Passkey support in this app is
        coming soon.
      </div>
    );
  }
  if (available === null) {
    return <div className={styles.subtleHint}>Checking…</div>;
  }

  return (
    <>
      <div className={styles.subtleHint}>
        A passkey signs you in with Face ID, Touch ID, or your device
        PIN — no password to type or steal.
      </div>

      {creds.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
          {creds.map((c) => (
            <div
              key={c.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                padding: "8px 10px",
                border: "0.5px solid var(--line)",
                borderRadius: 10,
                background: "var(--bg-sunk)",
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                {c.label || "Passkey"}
              </span>
              <span style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)", flex: 1 }}>
                added {new Date(c.created_at).toLocaleDateString()}
                {c.last_used_at
                  ? ` · last used ${new Date(c.last_used_at).toLocaleDateString()}`
                  : ""}
              </span>
              <button
                type="button"
                className={styles.btnDanger}
                onClick={() => void remove(c)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className={styles.actionRow}
        style={{ marginTop: 10, alignItems: "center", gap: 8 }}
      >
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Name (optional) — e.g. iPhone"
          maxLength={100}
          style={{
            flex: 1,
            minWidth: 140,
            padding: "9px 10px",
            borderRadius: 8,
            border: "0.5px solid var(--line-strong)",
            background: "var(--bg-sunk)",
            color: "var(--ink)",
            fontSize: 13,
          }}
        />
        <button
          type="button"
          className={styles.btnSecondary}
          disabled={busy}
          onClick={() => void addPasskey()}
        >
          {busy ? "Working…" : "Add a passkey"}
        </button>
      </div>

      {error && (
        <div
          style={{ color: "var(--zone-urgent)", fontSize: "var(--type-floor)", marginTop: 8 }}
        >
          {error}
        </div>
      )}
      {notice && !error && (
        <div style={{ color: "var(--zone-ok)", fontSize: "var(--type-floor)", marginTop: 8 }}>
          {notice}
        </div>
      )}
    </>
  );
}
